import { useQuery } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import { fetchTaskRequest } from '../v1-client';
import type { SiteId, TaskRequestRecord } from '../v1';
import { taskRequestQueryKey } from './task-request-cache';

/**
 * The dispatch request behind a task.
 *
 * `staleTime: Infinity` because a dispatch intent is IMMUTABLE once
 * written: it records what was asked for, which cannot change afterwards.
 * One fetch per drawer open, and none per SSE tick -- the drawer re-renders
 * on every live task update and this must not follow it.
 *
 * `retry: false` because the interesting failure is a 404, which the client
 * already turns into `null`; anything else is worth showing once rather
 * than three times.
 *
 * `background` is for a reader that is one of MANY at once -- a table
 * cell -- where a failure is shown as a dash and nobody is waiting on a
 * retry. It turns off every automatic re-ask of a failed read (on
 * remount, window focus, reconnect): `staleTime` cannot stop those for
 * an ERROR, which has no data to be fresh, so a table whose reads had
 * failed would otherwise re-ask all of them together every time the tab
 * regained focus.
 */
export function useTaskRequest(taskId: string | null, opts?: { background?: boolean }) {
  const siteId = useSafeSiteId();
  return useQuery<TaskRequestRecord | null>({
    queryKey: taskRequestQueryKey(siteId, taskId),
    queryFn: () => fetchTaskRequest(siteId as SiteId, taskId as string),
    enabled: Boolean(siteId && taskId),
    staleTime: Infinity,
    retry: false,
    ...(opts?.background
      ? { retryOnMount: false, refetchOnWindowFocus: false, refetchOnReconnect: false }
      : {}),
  });
}
